'use client';
import { authorizeSetupIntent } from '@/components/common/SinglePageApplication/checkoutSession';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { SIGNUP_ROUTES } from '@/const/signup';
import {
  AUTH_CHARGE_MIN_CENTS,
  shouldAuthorizeCard
} from '@/const/auth_charge';
import {
  ApplicationFlow,
  APPLICATION_FLOW_VARIANT
} from '@/const/application_flow';
import { FLAG_KEYS } from '@/const/flags';
import { POSTAL_CODE_MISMATCH } from '@/const/stripe';
import { RawEPCCCartResponse } from '@/types/Cart';
import { SignupState as RegistrationValues } from '@/types/Signup';
import { InsuranceProduct, SubscriptionProduct } from '@/types/Subscription';
import analytics from '@/util/analytics';
import { isBrandAmbassadorCode } from '@/util/form_validation_errors';
import { getGclid } from '@/util/google_tracking_client';
import { getMetaTrackingCookies } from '@/util/meta_tracking_client';
import { sleep } from '@/util/misc';
import {
  attachPaymentMethodToNewCustomer,
  isStripeDecline
} from '@/util/stripe_client';
import { createUser } from '@/util/user_client';
import { clearPersistedSignupState } from '@/components/common/SinglePageApplication/useSignupPersistence';

import { useDodgeballProvider } from '@/components/context/DodgeballContext/DodgeballContext';
import { useSignupAnalytics } from '@/components/hook/analytics';
import { useAmbassadorRedirectFlag } from '@/components/hook/flags';
import { useSignup } from '@/components/hook/signup';
import { useUser } from '@/components/hook/user';

/**
 * Where the Stripe customer comes from.
 *
 * `card-token` is the multi-page flow: the browser tokenized a card and the
 * customer does not exist yet, so submitting has to create it and attach the
 * token in one call.
 *
 * `checkout-session` is Embedded Checkout: the customer was created
 * server-side before the payment sheet opened and the SetupIntent has already
 * attached the payment method to it. Nothing is left to attach — which is
 * exactly what makes wallets possible, since Apple Pay and Google Pay never
 * hand back a token that could be carried to a later step.
 */
export type PaymentSource =
  | { kind: 'card-token' }
  | { kind: 'checkout-session'; customerId: string; setupIntentId: string };

type UseApplicationSubmitOptions = {
  /** Attributes conversion events to the flow the applicant came through. */
  applicationFlow: ApplicationFlow;
  /**
   * How the applicant paid ('card', 'apple_pay', …), sent on the submit and
   * purchase events. The multi-page flow doesn't set it.
   */
  paymentMethod?: string;
  user: RegistrationValues;
  subscriptionData: SubscriptionProduct[];
  insuranceData: InsuranceProduct[];
  previewCart?: RawEPCCCartResponse;
  stripeCouponId?: string;
  /** From the preview cart: whether the applied promo code requires a card authorization. */
  requiresAuthorizationCharge?: boolean;
  /**
   * Called with the submitted values when the card's postal-code check fails, in
   * place of the generic payment error, so the form can show the address.
   */
  onPostalCodeMismatch?: (submitted: RegistrationValues) => void;
};

/**
 * The signup submission pipeline, shared by the multi-page review step and the
 * single-page application.
 *
 * It provisions the account and hands off to `/apply/success`, which reads the
 * `signup_data` it writes to `sessionStorage` and completes login, membership,
 * and SMS opt-in from there. That hand-off is a contract: `/apply/success` is
 * useless without this payload, so it is written before the redirect on every
 * path.
 */
export function useApplicationSubmit(options: UseApplicationSubmitOptions) {
  const {
    applicationFlow,
    paymentMethod,
    user,
    subscriptionData,
    insuranceData,
    previewCart,
    stripeCouponId,
    requiresAuthorizationCharge,
    onPostalCodeMismatch
  } = options;

  const router = useRouter();
  const { actions: userActions } = useUser();
  const { actions: signupActions } = useSignup();
  const dodgeball = useDodgeballProvider();
  const { metadata: userSignupAnalytics } = useSignupAnalytics(user);
  const ambassadorRedirectModalEnabled = useAmbassadorRedirectFlag();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * Creates the EPCC user and its addresses. Returns the EPCC userId and the
   * Stripe customerId, whichever way the customer came into existence.
   */
  const registerUser = async (
    user: RegistrationValues,
    payment: PaymentSource
  ): Promise<{ userId: string; customerId: string }> => {
    const { fbc, fbp } = getMetaTrackingCookies();
    const gclid = getGclid();

    // TODO: Remove when Inngest workflow is implemented (see VIVE-XXXX)
    // Temporary solution to capture registration metadata until automated workflow is in place
    const internalNotes = [
      '------- Temporary Notes -------',
      `Tier chosen: ${user.plan?.name}`,
      `Insurance chosen: ${user.insurance_level}`,
      `Addon chosen: ${user.addon_subscription}`,
      `Billing Address: ${user.billingAddress}`,
      `Phone number: ${user.phone}`,
      '',
      `Address: ${user.address}`,
      `City: ${user.city}`,
      `State: ${user.state}`,
      `Zip: ${user.postalCode}`
    ].join('\n');

    // Both paths authorize the card: the token path in the attach route, the
    // SetupIntent path through the authorize route.
    if (payment.kind === 'checkout-session') {
      await authorizeSetupIntent(payment.setupIntentId, user);
    }

    const customerId =
      payment.kind === 'checkout-session'
        ? payment.customerId
        : (await attachPaymentMethodToNewCustomer(user, stripeCouponId))
            .customerId;

    const createUserResponse = await createUser({
      user: {
        ...user,
        internal_notes: internalNotes,
        fbc,
        fbp,
        gclid
      },
      stripe_customer_id: customerId,
      card_type: user.stripePaymentMethodBrand,
      ...(dodgeball.sourceToken
        ? { dodgeballSourceToken: dodgeball.sourceToken }
        : {})
    });

    const userId = createUserResponse?.data?.id;

    if (!userId) {
      console.error(createUserResponse);
      throw Error(`Failed to create a new user`);
    }

    // The shipping and billing addresses are created inside /api/user/create,
    // for the customer id EPCC just minted (stripe_customer_id is still updated
    // after login).

    return { userId, customerId };
  };

  const submitApplication = async (
    user: RegistrationValues,
    payment: PaymentSource = { kind: 'card-token' }
  ) => {
    // Backstop: ambassador codes are only valid through the dedicated
    // ambassador application, not this shared multi-page/single-page path -
    // gated on the same redirect-modal flag as every other ambassador-code
    // check (SignupStep1Form, SignUpApp, SignUpSummaryModal), so this stays
    // consistent with them when the flag is off.
    // A visible error, not a silent return - the single-page application has
    // no earlier guard of its own (unlike the multi-page flow's step 1 and
    // summary modal), so this can be the first and only signal a member with
    // an ambassador code gets, potentially after a card is already authorized.
    if (
      ambassadorRedirectModalEnabled &&
      isBrandAmbassadorCode(user.referralCode)
    ) {
      setErrorMessage(
        "This referral code isn't valid for this application. Please remove it, or apply through our Ambassador Program instead."
      );
      setIsSubmitting(false);
      return;
    }

    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      const { userId, customerId } = await registerUser(user, payment);

      // Store data needed to complete signup on success page
      const basePlanId = user.plan?.stripePlanId;
      const insurancePlanId = insuranceData.find(
        (plan) => plan.slug === user.insurance_level
      )?.stripePlanId;
      const subscriptionPlanIds = [basePlanId];
      const addonPlanId = subscriptionData.find(
        (plan) => plan.slug === user.addon_subscription
      )?.stripePlanId;
      if (addonPlanId) {
        subscriptionPlanIds.push(addonPlanId);
      }

      const conversionValue = previewCart?.data?.meta.display_price
        .without_discount.amount
        ? (previewCart.data.meta.display_price.without_discount
            .amount as number) / 100
        : 1;

      sessionStorage.setItem(
        'signup_data',
        JSON.stringify({
          credentials: { userId, email: user.email, password: user.password },
          membership: {
            subscriptionPlanIds,
            insurancePlanId,
            referralCode: user.referralCode
          },
          conversionValue,
          optinText: user.optinText,
          stripeCustomerId: customerId // Store for update after login
        })
      );

      let cakeDetails: Object = {};

      if (user.referralCode) {
        // Formatting Cake data for promo tracking with Segment
        cakeDetails = {
          checkout: {
            discountApplications: [
              {
                // Referral data
                title: user.referralCode,
                type: 'Referral code',
                value: {
                  amount: 200,
                  currencyCode: 'USD'
                }
              }
            ],
            lineItems: [
              {
                // Subscription data
                id: user.plan?.id,
                quantity: 1,
                title: user.plan?.name
              }
            ]
          }
        };
      }

      const { fbc, fbp } = getMetaTrackingCookies();
      const gclid = getGclid();

      const applicationEventProperties = {
        application_flow: applicationFlow,
        experiment_name: FLAG_KEYS.APPLY_SINGLE_PAGE,
        variant_name: APPLICATION_FLOW_VARIANT[applicationFlow],
        payment_method: paymentMethod,
        success: userId ? true : false,
        userId,
        userName: `${user.firstName} ${user.lastName}`,
        referralCode: user.referralCode,
        value: conversionValue,
        currency: 'USD',
        purchaseType: 'Application Submit',
        cakeDetails,
        ...userSignupAnalytics,
        email: user.email,
        phone: user.phone,
        firstName: user.firstName,
        lastName: user.lastName,
        fbc,
        fbp,
        gclid,
        eventSourceUrl: window.location.href
      };

      // Each event needs a unique messageId so Segment doesn't deduplicate
      // them against each other. CAPI's event_id is mapped to
      // properties.transactionId in Segment, so it must match messageId
      // for Pixel + CAPI deduplication to work.
      const submitId = `${userId}-submit`;
      const purchaseId = `${userId}-purchase`;

      console.warn('[segment] Application - Submit Application', {
        ...applicationEventProperties,
        transactionId: submitId
      });
      analytics.track(
        'Application - Submit Application',
        { ...applicationEventProperties, transactionId: submitId },
        { messageId: submitId }
      );

      console.warn('[segment] Application - Purchase', {
        ...applicationEventProperties,
        transactionId: purchaseId
      });
      analytics.track(
        'Application - Purchase',
        { ...applicationEventProperties, transactionId: purchaseId },
        { messageId: purchaseId }
      );
    } catch (error: any) {
      (isStripeDecline(error) ? console.warn : console.error)(
        '[Application Submission Error]',
        error
      );

      // Emit auth charge failure event whenever an authorization was attempted,
      // by either trigger, and Stripe rejected it.
      if (
        shouldAuthorizeCard(user.utm_source, requiresAuthorizationCharge) &&
        error.cause === 'Stripe'
      ) {
        const subscriptionPriceInCents = user.plan?.price?.asNumber ?? 0;
        const computedAuthChargeAmount =
          subscriptionPriceInCents > 0
            ? Math.max(subscriptionPriceInCents, AUTH_CHARGE_MIN_CENTS)
            : 0;

        const { fbc: failFbc, fbp: failFbp } = getMetaTrackingCookies();

        const purchaseFailedProperties = {
          application_flow: applicationFlow,
          userName: `${user.firstName} ${user.lastName}`,
          email: user.email,
          phone: user.phone,
          firstName: user.firstName,
          lastName: user.lastName,
          utm_source: user.utm_source,
          referralCode: user.referralCode || null,
          value: previewCart?.data?.meta.display_price.without_discount.amount
            ? (previewCart?.data?.meta.display_price.without_discount
                .amount as number) / 100
            : 1,
          currency: 'USD',
          purchaseType: 'Application Submit',
          subscription_price: subscriptionPriceInCents,
          computed_auth_charge_amount: computedAuthChargeAmount,
          failure_reason: error.message || 'Unknown',
          error_code: error.code || null,
          ...userSignupAnalytics,
          fbc: failFbc,
          fbp: failFbp,
          eventSourceUrl: window.location.href
        };
        console.warn('[segment] Purchase (Failed)', purchaseFailedProperties);
        analytics.track('Purchase (Failed)', purchaseFailedProperties);
      }

      if (error.code === POSTAL_CODE_MISMATCH && onPostalCodeMismatch) {
        // The form shows its own notice next to the address to fix.
        onPostalCodeMismatch(user);
      } else if (error.cause === 'Stripe') {
        setErrorMessage(
          'We were unable to validate your payment method. Please try a different card.'
        );
      } else {
        setErrorMessage(
          'There was an error, please check your selections and try again.'
        );
      }

      console.warn('[segment] Application Submission Error', {
        error,
        application_flow: applicationFlow
      });
      analytics.track('Application Submission Error', {
        error,
        application_flow: applicationFlow
      });
      setIsSubmitting(false);
      return;
    }

    // Store credentials temporarily for login on success page
    sessionStorage.setItem(
      'signup_credentials',
      JSON.stringify({
        email: user.email,
        password: user.password
      })
    );

    // A submitted application's draft has to go now — `resetForm()` below
    // clears `SignupContext`, but that happens after the page has already
    // navigated away, so the persistence hook never gets a render to write
    // the cleared state back on its own.
    clearPersistedSignupState();

    router.push(SIGNUP_ROUTES.SUCCESS);

    // Wait a bit before we reset, else the page may prematurely reload to first page of application
    await sleep(5000);

    signupActions.resetForm();

    // If user opted into SMS, refresh user so we can retrieve updated SMS settings
    // (Purposely refreshed after a delay to prevent race condition)
    if (user.optinText) {
      userActions.refresh();
    }
  };

  return {
    submitApplication,
    errorMessage,
    setErrorMessage,
    isSubmitting,
    setIsSubmitting
  };
}
